// Module: notificationApi
// Purpose: Gateway-only API functions for user notification history — list, mark-read, mark-all-read.
//          All requests go through the Auth Gateway (auth:8000) via the shared axios client.

import api from '../services/api';

// --> Helpers

/**
 * Unwrap the DeeTrade {status, data, error} envelope.
 * Throws if the backend signals an error.
 */
function unwrap(response) {
  const body = response.data;
  if (body && body.status === 'error') {
    const err = new Error(
      (body.error && body.error.message) || 'Notification API error'
    );
    err.code = body.error && body.error.code;
    throw err;
  }
  // Some endpoints return data directly in body.data; others return body directly.
  return body && body.data !== undefined ? body.data : body;
}

// --> Public API

/**
 * Fetch notifications with optional filters.
 *
 * @param {Object} opts
 * @param {number} [opts.limit=20]   - Max items to return.
 * @param {number} [opts.offset=0]   - Pagination offset.
 * @param {boolean} [opts.unread=false] - When true, returns only unread items.
 * @returns {Promise<{items: Array, total: number, limit: number, offset: number}>}
 */
export async function getNotifications({ limit = 20, offset = 0, unread = false } = {}) {
  const params = { limit, offset };
  if (unread) params.unread = true;
  const response = await api.get('/notifications', { params });
  return unwrap(response);
}

/**
 * Mark a single notification as read.
 *
 * @param {string|number} notificationId
 * @returns {Promise<Object>} Updated notification object.
 */
export async function markRead(notificationId) {
  const response = await api.patch(`/notifications/${notificationId}/read`);
  return unwrap(response);
}

/**
 * Mark all notifications as read for the authenticated user.
 *
 * @returns {Promise<Object>} Backend confirmation payload.
 */
export async function markAllRead() {
  const response = await api.post('/notifications/read-all');
  return unwrap(response);
}
